import {
  GranteeType,
  OperationType,
  READABLE_KYC_STATUS,
  RevokePoolTokenKYCTokenFormData,
  TABLE_HEADER_NAMES,
  Token,
  TokenWhitelistPoolTokensData,
} from '@archax/shared-types'
import WarningIcon from '@mui/icons-material/Warning'
import { Box, Button, Grid, Tooltip, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { ColDef, GridOptions, ICellRendererParams, ValueGetterParams } from 'ag-grid-community'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { createOperation } from '../../../api/operations'
import {
  GetTokenWhitelistParams,
  getTokenWhitelistPoolTokens,
  getTokenWhitelistPoolTokensCSVUrl,
} from '../../../api/tokens'
import Dialog from '../../../components/Dialog/Dialog'
import { ServerSideGrid } from '../../../components/ServerSideGrid'
import { AddButton } from '../../../components/ui/AddButton'
import { useGlobalContext } from '../../../context'
import { substringFilterParams } from '../../../util/common-grid-options'
import onApiError from '../../../util/on-api-error'
import tokenBalanceFormatter from '../../../util/token-balance-formatter'
import { hasUserRole } from '../../../util/user-roles'
import { GrantKYCTokenForm } from './GrantKYCTokenForm'
import { MintToTokenForm } from './MintToTokenForm'
import { SendNFTTokenForm } from './SendNFTTokenForm'
import { SendTokenForm } from './SendTokenForm'
import { WipeNFTTokenForm } from './WipeNFTTokenForm'
import { WipeTokenForm } from './WipeTokenForm'
import { extractTokenHolderAddress } from '../../../util/extract-token-address'
interface TokenPoolTokensWhitelistProps {
  token: Token
  isNft: boolean
  canMint: boolean
  canSend: boolean
}

interface RevokeKYCOperationData {
  formData: RevokePoolTokenKYCTokenFormData
}

function TokenWhitelistPoolTokens({ token, isNft, canMint, canSend }: TokenPoolTokensWhitelistProps) {
  const { id } = token
  const [showSendTokenDialog, setShowSendTokenDialog] = useState(false)
  const [showMintToTokenDialog, setShowMintToTokenDialog] = useState(false)
  const [showWipeTokenDialog, setShowWipeTokenDialog] = useState(false)
  const [showGrantKYCTokenDialog, setShowGrantKYCTokenDialog] = useState(false)
  const [showRevokeKYCTokenDialog, setShowRevokeKYCTokenDialog] = useState(false)
  const [selectedRow, setSelectedRow] = useState<Token | null>(null)
  const {
    state: { user },
  } = useGlobalContext()

  const { mutate: revokeKYCMutation } = useMutation(
    (data: RevokeKYCOperationData) => createOperation(OperationType.RevokeKYC, data.formData, id),
    {
      onSuccess: () => {
        toast.success('Revoke KYC request sent for approval')
      },
      onError: onApiError,
    },
  )

  const handleRevokeKYC = async (row: Token) => {
    const formData = { poolTokenId: row.id }
    revokeKYCMutation({ formData })
    setShowRevokeKYCTokenDialog(false)
  }

  const columnDefs: ColDef<TokenWhitelistPoolTokensData>[] = useMemo(
    () => [
      {
        field: 'status',
        headerName: TABLE_HEADER_NAMES.token_whitelist.is_whitelisted,
        minWidth: 130,
        valueGetter: (params: ValueGetterParams<TokenWhitelistPoolTokensData>) => {
          return params.data!.isWhitelisted ? READABLE_KYC_STATUS.ENABLED : READABLE_KYC_STATUS.DISABLED
        },
        sortable: false,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: Object.values(READABLE_KYC_STATUS),
        },
      },
      {
        field: 'pool_token_name',
        headerName: TABLE_HEADER_NAMES.common.token_name,
        flex: 1,
        minWidth: 160,
        valueGetter: (params: ValueGetterParams<TokenWhitelistPoolTokensData>) => {
          return params.data!.whitelistedPoolToken.name
        },
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: substringFilterParams,
      },
      {
        field: 'onChainData.balance',
        headerName: TABLE_HEADER_NAMES.common.balance,
        flex: 1,
        minWidth: 130,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<TokenWhitelistPoolTokensData>) => {
          if (params.data!.onChainData.balance === undefined || params.data!.onChainData.balance === null) {
            return (
              <Tooltip title="Could not retrieve the balance">
                <Typography>
                  <WarningIcon />
                </Typography>
              </Tooltip>
            )
          }

          return tokenBalanceFormatter(params.data!.onChainData.balance, token.decimals)
        },
      },
      {
        field: 'pool_token_address',
        headerName: TABLE_HEADER_NAMES.common.address,
        flex: 3,
        minWidth: 220,
        valueGetter: (params: ValueGetterParams<TokenWhitelistPoolTokensData>) => {
          return extractTokenHolderAddress(params.data!.whitelistedPoolToken)
        },
        sortable: true,
        filter: false,
      },
    ],
    [],
  )

  const userRoleColumnDefs: ColDef<TokenWhitelistPoolTokensData>[] = useMemo(
    () => [
      {
        field: 'actions',
        flex: 1,
        minWidth: 400,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<TokenWhitelistPoolTokensData>) => {
          const isWhitelisted = params.data!.isWhitelisted
          const canWipe = BigInt(params.data!.onChainData.balance) > BigInt(0) && !token.hasExternalOwner
          return (
            <Grid item xs={4}>
              <Box display="flex" justifyContent="flex-end">
                {isWhitelisted && (
                  <>
                    <Box marginRight={1}>
                      <Button
                        disabled={!canSend || token.hasExternalOwner}
                        variant="contained"
                        size="small"
                        onClick={() => {
                          setSelectedRow(params.data!.whitelistedPoolToken)
                          setShowSendTokenDialog(true)
                        }}
                      >
                        Send
                      </Button>
                    </Box>
                    <Box marginRight={1}>
                      <Button
                        disabled={!canMint || token.hasExternalOwner}
                        variant="contained"
                        size="small"
                        onClick={() => {
                          setSelectedRow(params.data!.whitelistedPoolToken)
                          setShowMintToTokenDialog(true)
                        }}
                      >
                        Mint
                      </Button>
                    </Box>
                  </>
                )}
                <Box marginRight={1}>
                  <Button
                    disabled={!canWipe}
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setSelectedRow(params.data!.whitelistedPoolToken)
                      setShowWipeTokenDialog(true)
                    }}
                  >
                    Wipe
                  </Button>
                </Box>
                <Box marginRight={1}>
                  <Button
                    sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setSelectedRow(params.data!.whitelistedPoolToken)
                      isWhitelisted ? setShowRevokeKYCTokenDialog(true) : setShowGrantKYCTokenDialog(true)
                    }}
                    disabled={token.hasExternalOwner}
                  >
                    {isWhitelisted ? 'Revoke KYC' : 'Grant KYC'}
                  </Button>
                </Box>
              </Box>
            </Grid>
          )
        },
      },
    ],
    [canMint, canSend],
  )

  const gridOptions: GridOptions = useMemo(
    () => ({
      columnDefs: hasUserRole(user) ? [...columnDefs, ...userRoleColumnDefs] : columnDefs,
    }),
    [columnDefs, userRoleColumnDefs, user],
  )

  return (
    <div>
      <Box
        sx={{ paddingLeft: 0 }}
        maxWidth="xl"
        flexDirection={'row'}
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Typography align="left" variant="h5">
          Pool tokens
        </Typography>
        <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
          {hasUserRole(user) && (
            <AddButton
              name="whitelist-addition"
              ariaLabel="Grant KYC"
              onClick={() => {
                setSelectedRow(null)
                setShowGrantKYCTokenDialog(true)
              }}
              disabled={token.hasExternalOwner}
            ></AddButton>
          )}
        </Box>
      </Box>
      <Box>
        <ServerSideGrid
          gridOptions={gridOptions}
          queryFn={(gridParams) => {
            return getTokenWhitelistPoolTokens({ ...(gridParams as GetTokenWhitelistParams), id })
          }}
          csvExportUrlGetter={(gridParams) => {
            return getTokenWhitelistPoolTokensCSVUrl({
              ...(gridParams as GetTokenWhitelistParams),
              id,
            })
          }}
        />
      </Box>
      <Dialog
        fullWidth
        maxWidth="sm"
        title={`Grant KYC`}
        onClose={() => setShowGrantKYCTokenDialog(false)}
        open={showGrantKYCTokenDialog}
      >
        <GrantKYCTokenForm
          granteeType={GranteeType.PoolToken}
          grantee={!!selectedRow?.id ? { value: selectedRow?.id, label: selectedRow?.name } : undefined}
          onSuccess={() => setShowGrantKYCTokenDialog(false)}
          token={token}
        />
      </Dialog>
      <Dialog
        title={`${isNft ? 'Send token to' : 'Send tokens to'}: ${selectedRow?.name}(${selectedRow?.name})`}
        onClose={() => setShowSendTokenDialog(false)}
        open={showSendTokenDialog}
      >
        {isNft && (
          <SendNFTTokenForm
            onSuccess={() => setShowSendTokenDialog(false)}
            tokenId={id}
            token={token}
            assets={token.onChainData?.assets!}
            poolToken={selectedRow!}
          />
        )}
        {!isNft && (
          <SendTokenForm onSuccess={() => setShowSendTokenDialog(false)} token={token} poolToken={selectedRow!} />
        )}
      </Dialog>
      <Dialog
        title={`${isNft ? 'Mint token to' : 'Mint tokens to'}: ${selectedRow?.name}(${selectedRow?.name})`}
        onClose={() => setShowMintToTokenDialog(false)}
        open={showMintToTokenDialog}
      >
        <MintToTokenForm
          onSuccess={() => setShowMintToTokenDialog(false)}
          token={token}
          poolToken={selectedRow!}
          isNft={isNft}
        />
      </Dialog>
      <Dialog
        title={`Wipe tokens from: ${selectedRow?.name}(${selectedRow?.symbol})`}
        onClose={() => setShowWipeTokenDialog(false)}
        open={showWipeTokenDialog}
      >
        {isNft && (
          <WipeNFTTokenForm onSuccess={() => setShowWipeTokenDialog(false)} tokenId={id} poolToken={selectedRow!} />
        )}
        {!isNft && (
          <WipeTokenForm onSuccess={() => setShowWipeTokenDialog(false)} token={token} poolToken={selectedRow!} />
        )}
      </Dialog>
      <Dialog
        title="Are you sure that you want to revoke account access to the token?"
        onConfirm={() => handleRevokeKYC(selectedRow!)}
        confirmLabel="Revoke KYC"
        onClose={() => setShowRevokeKYCTokenDialog(false)}
        open={showRevokeKYCTokenDialog}
        showCancel
      >
        <Typography variant="body2">You can grant the KYC back later.</Typography>
      </Dialog>
    </div>
  )
}

export default TokenWhitelistPoolTokens
